<template>
  <!-- Shown after an email sign-up: a verification email was sent server-side, so tell the user to
       check their inbox (and spam), let them resend, and continue into the app. -->
  <div>
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>
    <div v-if="noticeMessage" class="alert alert-success" role="status">
      {{ noticeMessage }}
    </div>

    <div class="sent-state text-center">
      <span class="material-icons sent-icon">mark_email_read</span>
      <h2 class="auth-heading mt-3 mb-2">{{ $t('on_boarding.verify_email_page.check_your_inbox') }}</h2>
      <p class="auth-sub">
        <template v-if="email">
          <i18n-t keypath="on_boarding.verify_email_page.we_sent_a_verification_email_to" scope="global">
            <template #email><strong class="sent-email">{{ email }}</strong></template>
          </i18n-t>
        </template>
        <template v-else>
          {{ $t('on_boarding.verify_email_page.we_sent_you_a_verification_email') }}
        </template>
        {{ $t('on_boarding.verify_email_page.it_can_take_a_minute_to') }}
      </p>

      <OpenMailboxButtons class="mailbox-buttons" />

      <button type="button" class="btn pro-btn pro-btn--accent w-100" @click="continueTapped">
        <span class="material-icons">arrow_forward</span>
        <span>{{ $t('on_boarding.verify_email_page.continue_to_hiiker') }}</span>
      </button>

      <p class="text-muted small mt-3 mb-0">
        {{ $t('on_boarding.verify_email_page.didn_t_receive_the_email') }}
        <a href="#" class="link-yellow" :class="{ disabled: resending }" @click.prevent="resend">
          {{ resending ? $t('on_boarding.verify_email_page.sending') : $t('on_boarding.verify_email_page.send_it_again') }}
        </a>
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OpenMailboxButtons from '../../../utils/shared_components/OpenMailboxButtons.vue';

export default defineComponent({
  name: 'OnBoardingVerifyEmailPage',
  components: { OpenMailboxButtons },
  data() {
    return {
      resending: false,
    };
  },
  computed: {
    email(): string | null {
      return this.$store.state.onBoarding.verifyEmailAddress;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    noticeMessage(): string | null {
      return this.$store.state.onBoarding.noticeMessage;
    },
  },
  methods: {
    continueTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/setNotice', null);
      this.$store.dispatch('onBoarding/setNextPostLoginPage');
    },
    async resend() {
      // Guard against double-taps hammering the send endpoint (Airship/rate limits).
      if (this.resending) return;
      this.resending = true;
      try {
        await this.$store.dispatch('onBoarding/resendVerificationEmail');
      } finally {
        // Brief cooldown so the link can't be spammed.
        setTimeout(() => { this.resending = false; }, 4000);
      }
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
    this.$store.commit('onBoarding/setNotice', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.5rem;
}
.auth-sub {
  color: #a1a4ad;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 1.25rem;
}
.sent-state {
  padding: 0.5rem 0 1rem;
}
.sent-icon {
  font-size: 3.5rem;
  color: var(--yellow, #fddf00);
  filter: drop-shadow(0 8px 24px rgba(253, 223, 0, 0.25));
}
.sent-email {
  color: #fff;
  font-weight: 700;
}
.mailbox-buttons {
  display: block;
  margin-bottom: 1.25rem;
}
.link-yellow {
  color: var(--yellow, #fddf00);
}
.link-yellow:hover {
  text-decoration: underline;
}
.link-yellow.disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>
